import "public/css/components.contents.scss";
import React from 'react';
import PropTypes from 'prop-types';
import ButtonRender from "./button";
import {Icon, Input, Form, Checkbox, Select} from "semantic-ui-react";
import MessageRender, { Message } from "./messages";
import ModuleConfiguration from "./model";
import { useSetMinHeight } from "pages/app/resize";
import useStateMachine from "./state";

// Delay in microsecond after submission before cleaning the form
const CLEAR_INTERVAL = 1500;

const DEFAULT_SMALLSCREEN = 600;

// hook to listen the window width
function useWindowWidth() {
    const [width, setWidth] = React.useState(0);
    const isSmallScreen = width < DEFAULT_SMALLSCREEN;

    React.useLayoutEffect(() => {
        function updateWidth() {
            setWidth(window.innerWidth);
        }
        window.addEventListener('resize', updateWidth);
        updateWidth();
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    return { width, isSmallScreen };
}

/** The component wrapping the input for all fields */
function GeneratedInput({
    name,
    configuration,
    values,
    onChange,
    children,
    type,
    icon,
    formError,
    ...props
}) {

    const setMinHeigth = useSetMinHeight();
    const recomputeSize = (ev, props) => {
        // Find the dropdown from the target element
        const target = ev.target.closest('.selection.dropdown').querySelector('.menu');
        // If the class list contains visible we are closing, we unset the forced min height
        if (target.classList.contains('visible')) {
            setMinHeigth(null);
            return;
        }
        // the event is called before the menu gets visible so we trick it by
        // waiting for the DOM modifications to get the height of the menu.
        setTimeout(() => {
            // Set the minimal heigh by additioning the position of the top of the menu
            // and the total length of the menu
            setMinHeigth(target.offsetHeight + target.scrollHeight);
        }, 0);
    };

    return <Form.Field>
        { configuration.types[name] === "choice" ?
            <Select
                style={ { borderRadius: configuration.btnDefault.borderRadius } }
                placeholder={ configuration.placeholders[name] || name }
                name={ name }
                onChange={ onChange }
                options={ configuration.choices[name] }
                upward={ false }
                onOpen={ recomputeSize }
                onClose={ recomputeSize }
            /> : configuration.types[name] === "bool" ?
                <div style={
                    { "text-align": configuration.checkboxPosition }
                }>
                    <Checkbox
                        name={ name }
                        label={ configuration.placeholders[name] || name }
                        onChange={ onChange }
                    />
                </div> : <Input
                    placeholder={ configuration.placeholders[name] || name }
                    iconPosition={ configuration.icon.active ? 'left' : null }
                    name={ name }
                    value={ values[name] || '' }
                    onChange={ onChange }
                    error={ (formError && !values[name]) ? true : null }
                    { ...props }
                >
                    { configuration.types[name] !== 'date' && configuration.icon.active && <Icon
                        name={ configuration.icon.name || icon || 'address book outline' }
                        style={ { color: configuration.icon.color } }
                    /> }
                    <input
                        className={ configuration.types[name] === 'date' && values[name] ? 'input-date-value' : '' }
                        style={ { borderRadius: configuration.btnDefault.borderRadius } }
                        type={ configuration.types[name] === 'date' ? 'date' : type }
                        max={ configuration.types[name] === 'date' ? "2050-01-01" : undefined }
                        lang='fr-FR'
                    />
                    { children || null }
                </Input>
        }

    </Form.Field>;
}

GeneratedInput.propTypes = {
    // Children is used to inject the button in the email input in the case the email field is alone
    children: PropTypes.node,
    name: PropTypes.string.isRequired,
    configuration: PropTypes.instanceOf(ModuleConfiguration).isRequired,
    // The object containing all the values of the form
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    type: PropTypes.string,
    icon: PropTypes.string,
    formError: PropTypes.bool,
};
GeneratedInput.defaultProps = {
    type: 'text',
};

export default function InputGenerator({
    configuration,
    onSubmit,
}) {
    // the form input and submission state machine
    const [ { isLoading, success, focus, values, invalid, formError }, dispatch ] = useStateMachine(configuration);

    // check if fields are empty (except type boolean)
    const requiredValues = configuration.fields.filter(f => configuration.types[f] !== 'bool')
        .map(f => values[f])
        .every(elem => elem !== undefined && elem !== '');

    // Submission handler
    const handleSubmit = React.useCallback(async () => {
        if (!requiredValues) {
            dispatch({ type: 'set-form-error'});
        } else {
            dispatch({ type: 'start-request'});
            const isSubscribed = await onSubmit(values);
            dispatch({ type: isSubscribed ? 'success' : 'failure' });

            if (!isSubscribed) {
                // Clear the form after the timeout
                setTimeout(() => dispatch({ type: 'clear' }), CLEAR_INTERVAL);
            }
        }
    }, [dispatch, onSubmit, values, requiredValues]);

    const { isSmallScreen } = useWindowWidth();

    // Callback when changing the value of an input
    const handleChange = React.useCallback((e, {value, name, checked}) => {
        dispatch({
            type: 'input',
            values: { [name] : checked !== undefined ? checked : value },
        });
    }, [dispatch]);

    // Follow the focus of the email field
    const setFocus = React.useCallback(() => dispatch({ type: 'focus' }), [dispatch]);
    const unsetFocus = React.useCallback(() => dispatch({ type: 'blur' }), [dispatch]);

    const button = <ButtonRender
        configuration={ configuration }
        onClick={ handleSubmit }
        disabled={ isLoading }
        loading={ isLoading }
        sendStatus={ success }
        isSmallScreen={ isSmallScreen }
    />;

    // Inject the button in the Email input if there is only the email input
    // else the button is render outside, after all the inputs.
    const renderOutside = configuration.fields.length > 1 || isSmallScreen;

    const emailInput = <GeneratedInput
        key="email"
        configuration={ configuration }
        onChange={ handleChange }
        values={ values }
        name="email"
        type="email"

        className={ configuration.successError === true && success ? "success" : null }
        action={ !configuration.separated }
        icon="at"
        iconPosition={ configuration.icon.active ? 'left' : null }
        error={ (configuration.successError && focus === false && ( invalid || success === false )) || (formError && !values['email']) }
        onBlur={ unsetFocus }
        onFocus={ setFocus }
        formError={ formError }
    >
        { !renderOutside && button }
    </GeneratedInput>;

    const inputs = configuration.fields.length <= 1
        ? emailInput
        : configuration.fields.map(name => name === 'email' ? emailInput : <GeneratedInput
            key={ name }
            configuration={ configuration }
            name={ name }
            values={ values }
            onChange={ handleChange }
            formError={ formError }
        />);

    return <div className="input-content-block">
        { inputs }
        { renderOutside && button }
        { configuration.validation && <MessageRender
            // render invalid message if the focus was set a least once
            invalid={ focus === false && invalid }
            sendStatus={ success }
            configuration={ configuration }
        /> }
        { formError && !requiredValues &&
            <Message
                type="error"
                text={ configuration.errorMessage.text }
                style={ configuration.errorMessage.style }
            >
            All fields are required
            </Message>
        }
    </div>;
};

InputGenerator.propTypes = {
    configuration: PropTypes.instanceOf(ModuleConfiguration).isRequired,
    onSubmit: PropTypes.func.isRequired,
};
